import type { FileDto, KnowledgeItemDto } from '@fos/shared/contracts/knowledge';
import { Download, Paperclip, Trash2, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { checkFileBeforeUpload, uploadFile } from '../../../lib/files-api';
import { formatDateShort } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { Button, IconButton } from '../../../ui/Button';
import { Dialog } from '../../../ui/Dialog';
import { SectionHeader } from '../../../ui/PageHeader';
import { Section } from '../../../ui/Section';
import { EmptyState } from '../../../ui/States';
import { toast } from '../../../ui/Toast';
import { TOUCH_BUTTON } from '../../mobile';
import { formatFileSize } from '../knowledge-format';

/** Wiersz pliku: nazwa (pobranie jako załącznik), typ, rozmiar, data; opcjonalnie „Usuń” (archiwizacja). */
export function FileRow({ file, onRemove }: { file: FileDto; onRemove?: () => void }) {
  return (
    <li className="flex min-h-[52px] items-center gap-3 border-b border-line py-2">
      <Paperclip size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
      <div className="flex min-w-0 flex-1 flex-col">
        <a
          href={file.downloadUrl}
          download={file.filename}
          className="truncate rounded-xs text-body-s font-medium text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent"
        >
          {file.filename}
        </a>
        <span className="truncate text-caption text-fg-muted">
          {file.typeLabel} · {formatFileSize(file.sizeBytes)} · {formatDateShort(file.createdAt)}
          {file.uploadedBy ? ` · ${file.uploadedBy.name}` : ''}
        </span>
      </div>
      <a
        href={file.downloadUrl}
        download={file.filename}
        aria-label={`Pobierz ${file.filename}`}
        className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-fg-secondary hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent"
      >
        <Download size={16} strokeWidth={1.75} aria-hidden />
      </a>
      {onRemove && (
        <IconButton icon={Trash2} label={`Usuń ${file.filename}`} onClick={onRemove} className="max-md:size-11" />
      )}
    </li>
  );
}

/**
 * Załączniki rekordu wiedzy: lista, „Dodaj plik” (REST `POST /api/files` z właścicielem = rekord; klient i projekt
 * dziedziczone z rekordu), „Usuń” = archiwizacja (plik znika z listy, treść zostaje w magazynie).
 */
export function AttachmentsSection({ item }: { item: KnowledgeItemDto }) {
  const utils = trpc.useUtils();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [toRemove, setToRemove] = useState<FileDto | null>(null);
  const archive = trpc.knowledge.archiveFile.useMutation({
    // dialog zamyka się po odświeżeniu listy: fokus nie wraca na usunięty wiersz (useReturnFocus → nagłówek strony)
    onSuccess: async () => {
      toast.success('Plik usunięty', { description: 'Trafił do archiwum.' });
      await utils.knowledge.get.invalidate({ id: item.id });
    },
    onError: (err) => toast.error('Nie udało się usunąć pliku', { description: errorMessage(err) }),
    onSettled: () => setToRemove(null),
  });

  const onPick = async (file: File | null) => {
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    const problem = checkFileBeforeUpload(file);
    if (problem) {
      toast.error('Nie można dodać pliku', { description: problem });
      return;
    }
    setUploading(true);
    try {
      await uploadFile(file, { ownerType: 'knowledge_item', ownerId: item.id });
      toast.success('Plik dodany', { description: file.name });
      void utils.knowledge.get.invalidate({ id: item.id });
    } catch (err) {
      toast.error('Nie udało się dodać pliku', { description: errorMessage(err) });
    } finally {
      setUploading(false);
    }
  };

  const files = item.attachments;
  if (files.length === 0 && !item.canEdit) return null;
  return (
    <Section aria-label="Załączniki">
      <SectionHeader
        title="Załączniki"
        action={
          item.canEdit ? (
            <>
              <Button
                variant="ghost"
                size="s"
                icon={Upload}
                loading={uploading}
                onClick={() => inputRef.current?.click()}
                className={TOUCH_BUTTON}
              >
                Dodaj plik
              </Button>
              <input
                ref={inputRef}
                type="file"
                className="sr-only"
                tabIndex={-1}
                aria-hidden
                onChange={(e) => void onPick(e.target.files?.[0] ?? null)}
              />
            </>
          ) : undefined
        }
      />
      {files.length === 0 ? (
        <EmptyState
          compact
          icon={Paperclip}
          title="Brak załączników"
          description="PDF, obrazy, dokumenty Office, CSV i ZIP do 25 MB."
        />
      ) : (
        <ul aria-label="Załączniki">
          {files.map((f) => (
            <FileRow key={f.id} file={f} onRemove={item.canEdit ? () => setToRemove(f) : undefined} />
          ))}
        </ul>
      )}
      <Dialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        width="s"
        title="Usunąć plik?"
        description={
          toRemove
            ? `„${toRemove.filename}” zniknie z listy i nie będzie można go pobrać. Plik trafi do archiwum.`
            : undefined
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setToRemove(null)} className={TOUCH_BUTTON}>
              Anuluj
            </Button>
            <Button
              variant="primary"
              icon={Trash2}
              loading={archive.isPending}
              onClick={() => toRemove && archive.mutate({ id: toRemove.id })}
              className={TOUCH_BUTTON}
            >
              Usuń plik
            </Button>
          </>
        }
      />
    </Section>
  );
}
